import chalk from 'chalk';
import Table from 'cli-table3';

export type OutputFormat = 'json' | 'table';

export function formatOutput(data: unknown, format: OutputFormat): string {
  if (format === 'json') {
    return JSON.stringify(data, null, 2);
  }

  if (Array.isArray(data)) {
    return formatTable(data as Record<string, unknown>[]);
  }

  if (typeof data === 'object' && data !== null) {
    return formatKeyValue(data as Record<string, unknown>);
  }

  return String(data);
}

function formatTable(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return chalk.dim('No results');

  const keys = Object.keys(rows[0]!);
  const table = new Table({ head: keys.map(k => chalk.bold(k)) });

  for (const row of rows) {
    table.push(keys.map(k => {
      const val = row[k];
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    }));
  }

  return table.toString();
}

function formatKeyValue(obj: Record<string, unknown>): string {
  const table = new Table();
  for (const [key, value] of Object.entries(obj)) {
    const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
    table.push({ [chalk.bold(key)]: displayValue });
  }
  return table.toString();
}

/**
 * Human (table) rendering for a `rekor batch` response (#354).
 *
 * The success body is `{ batch_id, results: [...] }` — an object, so the generic
 * `formatKeyValue` path JSON-stringifies the entire `results` array into a single
 * unreadable cell. This renders one legible row per operation (index, type, and the
 * resulting id/outcome) plus a `batch_id` summary line. JSON output is unchanged —
 * callers pass `--output json` through `formatOutput` for the full per-operation payload.
 *
 * Batch is atomic: a failing operation rolls the whole batch back and returns a 400
 * error envelope (`Operation N failed: …`, surfaced by handleCliError), so every entry
 * in `results` is a success — there are no per-op failure rows to render here.
 */
export function formatBatchResult(response: unknown): string {
  const obj = response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
  const results = Array.isArray(obj.results) ? (obj.results as Record<string, unknown>[]) : [];
  const batchId = typeof obj.batch_id === 'string' ? obj.batch_id : '(none)';
  const summary = chalk.dim(`batch_id: ${batchId} · ${results.length} operation${results.length === 1 ? '' : 's'}`);
  if (results.length === 0) return summary;

  const table = new Table({ head: ['#', 'operation', 'result'].map(k => chalk.bold(k)) });
  results.forEach((r, i) => {
    table.push([String(i), String(r?.type ?? ''), summarizeBatchResult(r)]);
  });
  return `${summary}\n${table.toString()}`;
}

/** One result row's human summary: the resulting entity's `<label>/<id>`, "deleted", or compact JSON. */
function summarizeBatchResult(r: Record<string, unknown>): string {
  if (!r || typeof r !== 'object') return String(r ?? '');
  if (r.deleted === true) return chalk.dim('deleted');
  const entity = r.document ?? r.relationship ?? r.record_type;
  if (entity && typeof entity === 'object') {
    const e = entity as Record<string, unknown>;
    const id = typeof e.id === 'string' ? e.id : undefined;
    const label =
      typeof e.record_type === 'string' ? e.record_type : // document → its record_type
      typeof e.rel_type === 'string' ? e.rel_type :      // relationship → its type
      typeof e.name === 'string' ? e.name :              // record_type → its name
      undefined;
    const version = e.version != null ? ` v${String(e.version)}` : '';
    if (id && label) return `${label}/${id}${version}`;
    if (id) return `${id}${version}`;
  }
  // No recognizable shape — compact JSON of THIS single result (never the whole array).
  return JSON.stringify(r);
}
